import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { alpha, FormControl, InputLabel } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useState } from "react";

dayjs.extend(utc);

interface FormFieldDateProps {
  label: string;
  value: string;
  required: boolean;
  // eslint-disable-next-line react/require-default-props
  error?: boolean;
  onChange: (newValue: string | null) => void;
}

const FormFieldDate = (props: FormFieldDateProps) => {
  const { value, label, required, onChange, error } = props;

  const [internalValue, setInternalValue] = useState<Dayjs | null>(
    value ? dayjs(value) : null,
  );

  useEffect(() => {
    setInternalValue(value ? dayjs(value) : null);
  }, [value]);

  const handleChange = (newValue: Dayjs | null) => {
    setInternalValue(newValue);

    if (newValue?.isValid() && newValue.year() > 1000) {
      const preservedUtcDate = dayjs
        .utc(newValue.format("YYYY-MM-DD"))
        .toISOString();
      onChange(preservedUtcDate);
    } else {
      onChange(null);
    }
  };

  return (
    <FormControl fullWidth required={required} variant="standard">
      <InputLabel
        shrink
        htmlFor={`${label}-input`}
        sx={{ position: "relative", marginBottom: "2px" }}
      >
        {label}
      </InputLabel>
      <DatePicker
        value={internalValue}
        onChange={handleChange}
        data-testId={`${label}-date-picker`}
        slotProps={{
          textField: {
            id: `${label}-input`,
            name: `${label}-input`,
            variant: "outlined",
            color: undefined,
            readOnly: false,
            sx: {
              fontSize: 14,
              "& .MuiPickersOutlinedInput-root": {
                height: 48,
                fontSize: 14,
                backgroundColor: "white",
                borderColor: "#E0E3E7",
                borderRadius: "6px",
                padding: "0 12px",
                "&:focus-within": {
                  borderColor: "#E0E3E7",
                  boxShadow: `${alpha(
                    error ? "#D32F2F" : "#00838F",
                    0.25,
                  )} 0 0 0 0.2rem`,
                },
                "&:hover-within": {
                  borderColor: "#00838F",
                  boxShadow: `${alpha(
                    error ? "#D32F2F" : "#00838F",
                    0.25,
                  )} 0 0 0 0.2rem`,
                },
              },
              "& .MuiSvgIcon-root": {
                color: (theme) => alpha(theme.palette.primary.main, 0.25),
              },
            },
          },
        }}
      />
    </FormControl>
  );
};

export default FormFieldDate;
